// Kept apart from the upload helper so the create form can recognise videos
// without pulling in the platform's library client.
import {studioText} from './studioText';
import type {Job} from './types';

/** Videos Studio transcribes: the types the platform library stores. */
export const VIDEO_TYPES: Record<string, string> = {
  mp4: 'video/mp4',
  mov: 'video/quicktime',
  webm: 'video/webm',
};
/** The only limit on an author's video: its file size, not its length. */
export const MAX_VIDEO_MB = 500;

const VIDEO_NAME = /\.(mp4|mov|webm)$/i;

/** The library type for a video file, from its name when the browser left the type blank. */
export const videoTypeFor = (file: File) =>
  Object.values(VIDEO_TYPES).includes(file.type)
    ? file.type
    : VIDEO_TYPES[file.name.split('.').pop()?.toLowerCase() || ''] || file.type;

/**
 * True when the job was made from a video. Once the server has read the source its kind says
 * so; while the video is still being transcribed, the job's library video or the file name on
 * the brief does. Documents can't have a video's name, since Studio refuses those files.
 */
export function isVideoJob(
  job?: Pick<Job, 'brief' | 'source' | 'sourceVideo'> | null,
): boolean {
  if (!job) return false;
  if (job.source?.kind) return job.source.kind === 'video';
  const name = job.brief?.documentName;
  return (
    !!job.sourceVideo ||
    (job.brief?.sourceType === 'document' &&
      typeof name === 'string' &&
      VIDEO_NAME.test(name.trim()))
  );
}

/**
 * True when the job's microreels are cut from its video (clipping): each scene is a clip that
 * plays the video itself. Older video jobs have no video mode and are animated.
 */
export function isClippingJob(
  job?: Pick<Job, 'brief' | 'source' | 'sourceVideo'> | null,
): boolean {
  return isVideoJob(job) && job?.brief?.videoMode === 'clipping';
}

/** A picture size as a CSS aspect ratio ("16 / 9" style); undefined when it is not known. */
export function aspectRatio(width?: number | null, height?: number | null) {
  return width && height && width > 0 && height > 0
    ? `${width} / ${height}`
    : undefined;
}

/**
 * The size clipping microreels export at, as the server works it out (VideoClips.OutputSize):
 * the video's own shape scaled down, never up, to fit 1920×1080, or 1080×1920 when it is
 * portrait, in even pixels. A video not measured yet counts as 1920×1080.
 */
export function clipOutputSize(width?: number | null, height?: number | null) {
  if (!width || !height || width <= 0 || height <= 0)
    return {width: 1920, height: 1080};
  const [maxW, maxH] = height > width ? [1080, 1920] : [1920, 1080];
  const scale = Math.min(1, maxW / width, maxH / height);
  const even = (v: number) => Math.max(2, Math.floor(v / 2 + 1e-6) * 2);
  return {width: even(width * scale), height: even(height * scale)};
}

// Servers before the video wording named a document in every source stage, videos included.
const VIDEO_STAGES: Array<[RegExp, string]> = [
  [/^Reading the document\b/, studioText.stageTranscribingVideo],
  [/^Finding the topics in the document\b/, studioText.stageFindingVideoTopics],
];

/** The server's stage text for the job; a video job's never mentions a document. */
export function stageText(job: Job): string {
  const stage = job.stage || '';
  return isVideoJob(job)
    ? VIDEO_STAGES.reduce(
        (text, [pattern, words]) => text.replace(pattern, words),
        stage,
      )
    : stage;
}
